import { useLayoutEffect, useRef } from "react";
import { animate, createScope } from "animejs";
import styles from "./animatedReveal.module.css";

function AnimatedReveal({ children, duration = 650, distance = 24, stagger: staggerStep = 100, className = "" }) {
  const rootRef = useRef(null);
  const revealedRef = useRef(new WeakSet());

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (typeof window.IntersectionObserver !== "function" ||
        typeof window.matchMedia !== "function") return;

    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (motion.matches) return;

    const scope = createScope({ root });
    const animations = new Map();
    let disposed = false;
    let motionDisabled = false;

    const createAnimation = (target, delay = 0, autoplay = false) => {
      scope.add(() => {
        animations.set(target, animate(target, {
          opacity: [0, 1],
          y: [distance, 0],
          duration,
          delay,
          ease: "out(3)",
          autoplay,
          onComplete: (completed) => completed.revert(),
        }));
      });
    };

    const observer = new window.IntersectionObserver((entries) => {
      if (disposed || motionDisabled) return;
      const entering = new Set(entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target));
      const targets = Array.from(animations.keys())
        .filter((target) => entering.has(target) && !revealedRef.current.has(target));

      targets.forEach((target, index) => {
        revealedRef.current.add(target);
        observer.unobserve(target);
        animations.get(target).revert();
        createAnimation(target, index * staggerStep, true);
      });
    }, { threshold: 0, rootMargin: "0px 0px -48px 0px" });

    const prepare = () => {
      if (disposed || motionDisabled) return;
      const targets = new Set(Array.from(root.querySelectorAll("[data-reveal]"))
        .filter((target) => target.closest("[data-reveal-root]") === root));

      animations.forEach((animation, target) => {
        if (targets.has(target)) return;
        observer.unobserve(target);
        animation.revert();
        animations.delete(target);
      });

      targets.forEach((target) => {
        if (animations.has(target) || revealedRef.current.has(target)) return;
        createAnimation(target);
        observer.observe(target);
      });
    };

    const mutations = typeof window.MutationObserver === "function"
      ? new window.MutationObserver(prepare)
      : null;

    const handleFocus = (event) => {
      const target = event.target.closest("[data-reveal]");
      if (!animations.has(target)) return;
      revealedRef.current.add(target);
      observer.unobserve(target);
      animations.get(target).revert();
    };
    const handleMotionChange = (event) => {
      if (!event.matches) return;
      motionDisabled = true;
      animations.forEach((animation, target) => revealedRef.current.add(target));
      observer.disconnect();
      mutations?.disconnect();
      scope.revert();
    };

    prepare();
    mutations?.observe(root, { childList: true, subtree: true });
    root.addEventListener("focusin", handleFocus);
    motion.addEventListener?.("change", handleMotionChange);

    return () => {
      disposed = true;
      observer.disconnect();
      mutations?.disconnect();
      root.removeEventListener("focusin", handleFocus);
      motion.removeEventListener?.("change", handleMotionChange);
      scope.revert();
    };
  }, [duration, distance, staggerStep]);

  return (
    <div ref={rootRef} data-reveal-root className={`${styles.root} ${className}`}>
      {children}
    </div>
  );
}

export default AnimatedReveal;